import config from '../configLoader';
import { EmployeeSearchTimesheetStatus } from '../interfaces/EmployeeInterface';
import { bucketLabel, type StatusKey } from '../Pages/EmployeeTimesheetReviewConsole';

/** The v2 status-pill vocabulary, shared with the board's BucketMeta.pill. */
export type SearchPillVariant = 'neutral' | 'warn' | 'info' | 'ok' | 'teal' | 'purple';

export interface MappedSearchStatus {
    bucket: StatusKey;
    variant: SearchPillVariant;
    label: string;
}

/** NPM's two-level approval — the tenants that show a 1st Approval stage. */
const managerApprovalOn = config.allowManagerApproval === true;

/**
 * Maps an employee-search timesheet status to the v2 board bucket (StatusKey), a
 * status-pill variant and a display label.
 *
 * This used to be copied into HomeV2 and TimesheetReviewBoardPage, and the copies had
 * drifted: supervisorApproved was `info` on Home and `purple` on the board. The board's
 * value wins here — it matches that stage's own tone ramp — and Home no longer renders
 * bucket pills at all now that its search box moved to the global header.
 *
 * Labels come from the shared `bucketLabel` resolver so a pill always reads the same
 * stage vocabulary as the board and the Home tiles (NPM: "1st / 2nd Approval Pending").
 *
 * Returns null for NoTimesheetForPayPeriod; the caller decides whether the zero-entry
 * gate makes that row openable anyway.
 */
export function mapSearchStatus(status: EmployeeSearchTimesheetStatus): MappedSearchStatus | null {
    switch (status) {
        case EmployeeSearchTimesheetStatus.Unsubmitted:
            return { bucket: 'unsubmitted', variant: 'neutral', label: bucketLabel('unsubmitted') };
        case EmployeeSearchTimesheetStatus.FirstLevelApproval:
            return managerApprovalOn
                ? { bucket: 'managerPending', variant: 'teal', label: bucketLabel('managerPending') }
                : { bucket: 'submitted', variant: 'warn', label: bucketLabel('submitted') };
        case EmployeeSearchTimesheetStatus.SecondLevelApproval:
            return { bucket: 'submitted', variant: 'warn', label: bucketLabel('submitted') };
        case EmployeeSearchTimesheetStatus.PayrollPending:
            return { bucket: 'supervisorApproved', variant: 'purple', label: bucketLabel('supervisorApproved') };
        case EmployeeSearchTimesheetStatus.PayrollApproved:
            return { bucket: 'payrollApproved', variant: 'ok', label: bucketLabel('payrollApproved') };
        case EmployeeSearchTimesheetStatus.NoTimesheetForPayPeriod:
        default:
            return null; // non-clickable unless the caller's zero-entry gate passes
    }
}
